import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Terminal, Copy, Check, ChevronRight, Menu, X,
  Shield, Server, Users, Zap, List, Activity, Settings, History,
  Laptop, UserCog, Key, Container, BookOpen, Globe, Lock, Download, Cpu,
  Apple
} from "lucide-react";
import Button from "../../components/ui/Button";
import { Link } from "react-router-dom";
import SEO from "../../components/shared/SEO";
import { useAuth } from "../../context/AuthContext";

// --- Components ---

const CodeBlock = ({ command, label, result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-6 group">
      <div className="relative bg-[#0d0d0d] rounded-t-xl border border-white/10 p-5 font-mono text-sm text-gray-300 shadow-2xl transition-all group-hover:border-orange-500/30 overflow-x-auto">
        <div className="flex justify-between items-center min-w-[300px]">
          <div className="flex items-center gap-3">
            <span className="text-orange-500 select-none font-bold">$</span>
            <span className="text-gray-100 font-medium tracking-tight">{command}</span>
          </div>
          <div className="flex items-center gap-4">
            {label && (
              <span className="text-[10px] text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded-md border border-orange-500/20 uppercase font-black tracking-widest hidden sm:block">
                {label}
              </span>
            )}
            <button
              onClick={handleCopy}
              className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-all"
            >
              {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
            </button>
          </div>
        </div>
      </div>
      {result && (
        <div className="bg-[#050505] border-x border-b border-white/10 rounded-b-xl p-4 text-xs md:text-sm text-gray-500 flex items-start gap-3">
          <div className="w-1.5 h-1.5 rounded-full bg-orange-500/40 mt-1.5 shrink-0" />
          <span className="italic leading-relaxed">{result}</span>
        </div>
      )}
    </div>
  );
};

const Section = ({ id, title, icon: Icon, description, children }) => (
  <section id={id} className="mb-32 scroll-mt-28">
    <div className="flex items-start gap-5 mb-10">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-600/10 border border-orange-500/20 flex items-center justify-center text-orange-500 shrink-0 shadow-lg shadow-orange-500/5">
        <Icon size={28} />
      </div>
      <div>
        <h2 className="text-3xl md:text-4xl font-black text-white mb-3 tracking-tight">{title}</h2>
        <p className="text-gray-400 text-lg leading-relaxed max-w-3xl">
          {description}
        </p>
      </div>
    </div>
    <div className="space-y-16">
      {children}
    </div>
  </section>
);

const SubSection = ({ title, children }) => (
  <div className="relative border-l border-white/10 pl-4 md:pl-8 transition-colors duration-300 group/sub">
    <div className="absolute top-0 left-[-1px] w-[1px] h-8 bg-gradient-to-b from-orange-500 to-transparent" />
    <h3 className="text-xl md:text-2xl font-bold text-gray-100 mb-4 tracking-tight group-hover/sub:text-orange-400 transition-colors">
      {title}
    </h3>
    <div className="text-gray-400 leading-relaxed">
      {children}
    </div>
  </div>
);

const navItems = [
  { id: 'setup', label: '1. The Setup', icon: Terminal },
  { id: 'workflow', label: '2. The Workflow', icon: Activity },
  { id: 'safety', label: '3. Safety Nets', icon: History },
  { id: 'team', label: '4. Team & Robots', icon: Users },
  { id: 'config', label: '5. Configuration', icon: Settings },
];

export default function PublicDocs() {
  const [activeSection, setActiveSection] = useState('setup');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth()

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        if (section && section.offsetTop <= scrollPosition && (section.offsetTop + section.offsetHeight) > scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Toast logic would go here
  };

  return (
    <><SEO
      title="Documentation"
      description="Learn how to integrate EnvDock CLI into your CI/CD pipelines and local workflows or terminals."
    />
      <div className="min-h-screen bg-[#050505] text-white selection:bg-orange-500/30 font-sans">

        {/* Background Decor */}
        <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
          <div className="absolute top-[-10%] left-[10%] w-[500px] h-[500px] bg-orange-600/10 rounded-full blur-[120px] opacity-50" />
          <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.1] mix-blend-overlay" />
        </div>

        {/* Navbar - Higher Z-index to prevent sidebar roll-over */}
        <nav className="fixed top-0 w-full z-[100] border-b border-white/5 bg-[#050505]/90 backdrop-blur-xl">
          <div className="container mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 font-black text-xl tracking-tighter">
              <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20">
                <Container size={18} className="text-white" strokeWidth={2.5} />
              </div>
              EnvDock <span className="text-xs font-mono text-orange-500 ml-1">DOCS</span>
            </Link>

            <div className="hidden md:flex items-center gap-4">
              {user ?
                <Link to="/dashboard/home">
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-500 text-white font-bold border-none rounded-lg px-6 shadow-xl shadow-white/5">
                    Dashboard
                  </Button>
                </Link> :
                <>
                  <Link to="/login" className="text-sm font-bold text-gray-400 hover:text-white">Sign In</Link>
                  <Link to="/register">
                    <Button size="sm" className="bg-orange-600 hover:bg-orange-500 text-white font-bold border-none rounded-lg px-6 shadow-xl shadow-white/5">
                      Get Started
                    </Button>
                  </Link>
                </>}
            </div>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-gray-400">
              {isMobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
          {/* --- Enhanced Mobile Menu --- */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="absolute top-0 left-0 w-full bg-[#0a0a0a] border-b border-white/10 md:hidden z-40 shadow-2xl"
              >
                <div className="flex flex-col p-8 gap-6 text-lg font-medium">
                  {
                    user ? 
                      <Link to="/dashboard/home" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full bg-orange-600 hover:bg-orange-500 text-white">Dashboard</Button>
                  </Link> 
                   :
                   <>
                  <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="text-gray-300">Sign In</Link>
                  <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="w-full bg-orange-600 hover:bg-orange-500 text-white">Get Started Free</Button>
                  </Link>
                   </>
                  }

                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </nav>

        <div className="container mx-auto px-6 pt-32 pb-24 flex flex-col lg:flex-row gap-16 relative">

          {/* SIDEBAR: self-start is critical for sticky to work in flex containers */}
          <aside className="hidden lg:block w-64 shrink-0 self-start sticky top-24 z-50">
            <div className="space-y-1 max-h-[calc(100vh-120px)] overflow-y-auto pr-4 custom-scrollbar">
              <div className="px-4 mb-4">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500">Navigation</span>
              </div>
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className={`group flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-bold transition-all border ${activeSection === item.id
                    ? 'bg-orange-500/10 border-orange-500/20 text-orange-500 shadow-inner'
                    : 'text-gray-500 border-transparent hover:text-gray-300 hover:bg-white/5'
                    }`}
                >
                  <item.icon size={18} className={activeSection === item.id ? 'text-orange-500' : 'text-gray-600 group-hover:text-gray-400'} />
                  {item.label}
                </button>
              ))}
            </div>
          </aside>

          <main className="flex-1 max-w-4xl min-w-0 z-10">
            <header className="mb-20">
              <h1 className="text-5xl md:text-7xl font-black mb-8 tracking-tighter leading-none">
                Developer <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-600 pr-2">Journey</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed max-w-2xl font-medium">
                A comprehensive guide to syncing, injecting, and managing your team's secrets via the CLI.
              </p>
            </header>

            <Section id="setup" title="The Setup" icon={Terminal} description="Bridging your machine to our zero-knowledge cloud.">

              <SubSection title="Package Manager (NPM)">
                <p className="mb-4">Recommended for most JavaScript and Node.js environments.</p>
                <CodeBlock command="npm install -g envdock-cli" result="Installs the CLI and symlinks 'edk' to your global path." />
              </SubSection>

              {/* <SubSection title="Standalone Binaries">
                <p className="mb-6">For servers or environments without Node.js, use our standalone compiled binaries.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <BinaryCard platform="macOS" arch="Universal" icon={Laptop} link="/download/macos" />
                  <BinaryCard platform="Linux" arch="x64 / ARM" icon={Cpu} link="/download/linux" />
                  <BinaryCard platform="Windows" arch="x64 (.exe)" icon={Globe} link="/download/windows" />
                </div>
                <p className="text-sm text-gray-500 mb-4">Manual Install (Unix):</p>
                <CodeBlock command="chmod +x envdock-binary && mv envdock-binary /usr/local/bin/edk" label="Setup" />
              </SubSection> */}
              <SubSection title="Standalone Binaries">
                <p className="mb-6 text-gray-400">
                  For environments without Node.js, use our standalone binaries. These are pre-compiled executables that bundle the required runtime.
                </p>

                <div className="space-y-6">
                  {/* macOS & Linux Section */}
                  <CodeBlock command="brew install envdock-spec/tap/edk" label={<div className="flex items-center gap-1"><Apple size={14} /> Mac-OS </div>} result="Installs the CLI and symlinks 'edk' to your global path." />
                  <CodeBlock command="curl -sL https://envdock.cloud/install | bash" label={<div className="flex items-center gap-1"><Terminal size={14} />  Linux (Bash)</div>} result="Installs the CLI and symlinks 'edk' to your global path." />

                  {/* Windows Section */}
                  <CodeBlock command="iwr https://envdock.cloud/install.ps1 -useb | iex" label={<div className="flex items-center gap-1"><Shield size={14} /> Windows (PowerShell)</div>} result="Installs the CLI and symlinks 'edk' to your global path." />


                </div>
              </SubSection>

              <SubSection title="Authentication">
                <CodeBlock command="edk login" label="Browser" result="Standard OAuth flow for personal machines." />
                <CodeBlock command="edk login user@example.com" label="Headless" result="Magic link authentication for remote servers." />
                <CodeBlock command="edk whoami" result="Displays active account details." />
                <CodeBlock command="edk logout" result="Securely flushes local session tokens." />
              </SubSection>

              <SubSection title="Project Initialization">
                <CodeBlock command="edk link" result="Interactive wizard to link this folder to a cloud project." />
                <CodeBlock command="edk status" label="Verify" result="Ensures local context matches cloud Project ID." />
              </SubSection>
            </Section>

            <Section id="workflow" title="The Workflow" icon={Activity} description="The core loop of secret management.">
              <SubSection title="Push & Pull">
                <CodeBlock command="edk pull" label="Dev" result="Decrypts and writes cloud secrets to your local .env" />
                <CodeBlock command="edk pull -e prod" label="Prod" result="Fetches production secrets (Requires Editor/Admin access)." />
                <CodeBlock command="edk push" label="Upload" result="Encrypts local .env and uploads a new version to the vault." />
              </SubSection>

              {/* <div className="relative p-8 rounded-3xl bg-[#090909] border border-orange-500/20 overflow-hidden group">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="text-orange-500" size={24} />
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">Zero-Disk Injection</h3>
              </div>
              <p className="text-gray-400 mb-6 leading-relaxed">Avoid the risk of .env files entirely by injecting variables into memory at runtime.</p>
              <CodeBlock command="edk run -- npm start" label="Node.js" result="Injects variables into process.env automatically." />
              <CodeBlock command="edk run -e prod -- python app.py" label="Python" />
            </div>  */}

              <div className="relative p-8 rounded-3xl bg-[#090909] border border-orange-500/20 overflow-hidden group">

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <Zap className="text-orange-500" size={24} />
                  <h3 className="text-2xl font-black text-white uppercase tracking-tighter">Universal Runtime Injection</h3>
                </div>

                {/* Description */}
                <p className="text-gray-400 mb-6 leading-relaxed">
                  We automatically detect your environment and inject the correct variable format at runtime. No need to rename secrets in the dashboard.
                </p>

                <CodeBlock command="edk run -- npm start" label="React.js" result="Injects variables into process.env automatically." />
                <CodeBlock command="edk run -- node server.js" label="Node.js" />
                <CodeBlock command="edk run -e prod -- python app.py" label="Python" />

                {/* Reference Table */}
                <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5 mb-6">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-white/5 text-gray-400">
                      <tr>
                        <th className="p-3 font-medium">Framework</th>
                        <th className="p-3 font-medium">Access Syntax</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10 text-gray-300">
                      <tr>
                        <td className="p-3">Node.js / Backend</td>
                        <td className="p-3 font-mono text-orange-400">process.env.API_URL</td>
                      </tr>
                      <tr>
                        <td className="p-3">React (CRA)</td>
                        <td className="p-3 font-mono text-orange-400">process.env.REACT_APP_API_URL</td>
                      </tr>
                      <tr>
                        <td className="p-3">Vite (Vue/React)</td>
                        <td className="p-3 font-mono text-orange-400">import.meta.env.VITE_API_URL</td>
                      </tr>
                      <tr>
                        <td className="p-3">Next.js (Client)</td>
                        <td className="p-3 font-mono text-orange-400">process.env.NEXT_PUBLIC_API_URL</td>
                      </tr>
                      <tr>
                        <td className="p-3">Python / Go</td>
                        <td className="p-3 font-mono text-orange-400">os.environ["API_URL"]</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Magic Feature Note */}
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4">
                  <div className="flex gap-2 text-orange-200 text-sm">
                    <span className="text-lg">✨</span>
                    <p>
                      <span className="font-bold text-white">Magic Feature:</span> You don't need to rename secrets! Just name it <code className="bg-black/30 px-1 py-0.5 rounded text-orange-400">API_URL</code> in EnvDock, and we auto-generate the prefixed versions (REACT_APP_, VITE_, etc.) instantly.
                    </p>
                  </div>
                </div>

              </div>
            </Section>

            <Section id="safety" title="Safety Nets" icon={History} description="Because mistakes in production shouldn't be permanent.">
              <SubSection title="Audit Logs">
                <CodeBlock command="edk history" result="Shows a chronological log of all changes." />
              </SubSection>
              <SubSection title="Version Control">
                <CodeBlock command="edk versions ls" label="Default" result="List snapshots for the development environment." />
                <CodeBlock command="edk versions ls -e prod" label="Prod" result="List snapshots for production environment." />
                <CodeBlock command="edk versions rollback" label="Rollback" result="Reverts dev to the previous version." />
                <CodeBlock command="edk versions rollback -e prod" label="Prod Rollback" result="Emergency rollback for production." />
              </SubSection>
            </Section>

            <Section id="team" title="Team & Robots" icon={Users} description="RBAC for teammates and service tokens for CI/CD.">
              <SubSection title="Human Access">
                <CodeBlock command="edk team ls" result="View all project members and their roles." />
                <CodeBlock command="edk team add dev@company.com" result="Add a team member with a specific role to project." />
                <CodeBlock command="edk team update user@id" label="Role Change" result="Promote or demote a user's permissions." />
                <CodeBlock command="edk team remove user@id" label="Revoke" result="Instantly remove a user's access." />
              </SubSection>

              <SubSection title="Service Tokens">
                <p className="mb-4">Use these for GitHub Actions, Vercel, or Docker containers.</p>
                <CodeBlock command="edk tokens create ci-runner" label="Generate" result="Shown once. Scoped strictly for read-only access." />
                <CodeBlock command="edk tokens ls" result="List active machine tokens." />
                <CodeBlock command="edk tokens revoke <token_id>" label="Emergency" result="Kill a compromised token immediately." />
              </SubSection>
            </Section>

            <Section id="config" title="Configuration" icon={Settings} description="Customize your local CLI behavior.">
              <SubSection title="Personal Settings">
                <CodeBlock command="edk config" result="Interactive menu for profile updates and local defaults." />
              </SubSection>
            </Section>
          </main>
        </div>

        <footer className="border-t border-white/5 py-12 bg-black/40 relative z-20">
          <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-gray-500 text-xs font-bold uppercase tracking-widest">
            <div className="flex items-center gap-2">
              <Container size={16} className="text-orange-500" /> EnvDock Systems Inc.
            </div>
            <div className="flex gap-8">
              <a href="#" className="hover:text-white transition-colors">Security</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </>

  );
}

const BinaryCard = ({ platform, arch, icon: Icon, link }) => (
  <a href={link} className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-orange-500/50 hover:bg-orange-500/5 transition-all group flex flex-col items-center text-center">
    <Icon size={24} className="text-gray-500 group-hover:text-orange-500 mb-3" />
    <span className="text-white font-bold text-sm">{platform}</span>
    <span className="text-[10px] text-gray-500 font-mono mt-1">{arch}</span>
    <div className="mt-4 flex items-center gap-1 text-[10px] font-black uppercase text-orange-500 tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
      <Download size={12} /> Get Binary
    </div>
  </a>
);